import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Button,
  Card,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { useStytchB2BClient } from '@stytch/react/b2b'
import {
  DiscoveredOrganization,
  Organization,
  OrganizationBySlugMatch,
} from '@stytch/vanilla-js'
import {
  getGlobalBaseUrl,
  getRedirectUrl,
  sanitizeReturnTo,
  SESSION_DURATION_MINUTES,
} from '@/utils/auth'
import { showErrorSnackbar } from '@/utils/snackbars'

type AuthenticateAccountsProps = {
  stytchOrg?: OrganizationBySlugMatch
}

export default function AuthenticateAccounts({
  stytchOrg,
}: AuthenticateAccountsProps) {
  const stytch = useStytchB2BClient()
  const navigate = useNavigate()
  const [discoveredOrgs, setDiscoveredOrgs] = useState<
    DiscoveredOrganization[]
  >([])
  const [searchParams, setSearchParams] = useSearchParams()

  const logInToOrg = useCallback(
    async (org: Organization) => {
      try {
        if (
          org.allowed_auth_methods?.length === 1 &&
          org.allowed_auth_methods[0] === 'sso' &&
          org.sso_default_connection_id
        ) {
          await stytch.sso.start({
            connection_id: org.sso_default_connection_id,
            login_redirect_url: getRedirectUrl(),
          })
        } else {
          await stytch.discovery.intermediateSessions.exchange({
            organization_id: org.organization_id,
            session_duration_minutes: SESSION_DURATION_MINUTES,
          })
        }
        const returnTo = searchParams.get('return_to')
        if (returnTo) {
          navigate(sanitizeReturnTo(returnTo))
        } else {
          setSearchParams()
        }
      } catch (e) {
        console.error('Error logging in to organization:', e)
        showErrorSnackbar(
          'An error occurred while logging in to the organization. Please try again.',
        )
        navigate('/authenticate', { replace: true })
      }
    },
    [
      navigate,
      searchParams,
      setSearchParams,
      stytch.discovery.intermediateSessions,
      stytch.sso,
    ],
  )

  const authenticate = useCallback(async () => {
    const token = searchParams.get('token')

    if (token) {
      const tokenType = searchParams.get('stytch_token_type')
      // 'sso' and 'multi_tenant_magic_links' authenticate directly into a
      // single org and never populate discoveredAccounts, even on success —
      // only the other token types run a discovery step that can come back
      // with no orgs at all.
      const isDiscoveryFlow =
        tokenType !== 'sso' && tokenType !== 'multi_tenant_magic_links'
      let discoveredAccounts: DiscoveredOrganization[] = []
      let succeeded = false
      try {
        if (tokenType === 'discovery') {
          const { discovered_organizations } =
            await stytch.magicLinks.discovery.authenticate({
              discovery_magic_links_token: token,
            })
          discoveredAccounts = discovered_organizations
        } else if (
          tokenType === 'sso' ||
          tokenType === 'multi_tenant_magic_links'
        ) {
          if (tokenType === 'sso') {
            await stytch.sso.authenticate({
              sso_token: token,
              session_duration_minutes: SESSION_DURATION_MINUTES,
            })
          } else {
            await stytch.magicLinks.authenticate({
              magic_links_token: token,
              session_duration_minutes: SESSION_DURATION_MINUTES,
            })
          }
        } else {
          const { discovered_organizations } =
            await stytch.discovery.organizations.list()
          discoveredAccounts = discovered_organizations
        }
        succeeded = true
      } catch {
        showErrorSnackbar("We couldn't complete your login. Please try again.")
        navigate('/authenticate', { replace: true })
      } finally {
        if (discoveredAccounts.length) {
          if (stytchOrg) {
            const matchedOrg = discoveredAccounts.find(
              (org) =>
                org.organization.organization_id === stytchOrg.organization_id,
            )
            if (matchedOrg) {
              logInToOrg(matchedOrg.organization)
            } else {
              // If the subdomain org doesn't match any discovered orgs, redirect to base/global URL
              window.location.replace(
                getGlobalBaseUrl(stytchOrg.organization_slug),
              )
            }
          } else if (discoveredAccounts.length === 1) {
            // Automatically log in to the single discovered organization
            logInToOrg(discoveredAccounts[0].organization)
          } else {
            setDiscoveredOrgs(discoveredAccounts)
          }
        } else if (succeeded && isDiscoveryFlow) {
          // The token was valid but the account isn't a member of any org.
          showErrorSnackbar(
            "You don't have access to any organization. Contact your admin for an invite.",
            { autoHideDuration: 5500 },
          )
          navigate('/authenticate', { replace: true })
        }
      }
    }
  }, [
    logInToOrg,
    navigate,
    searchParams,
    stytch.discovery.organizations,
    stytch.magicLinks,
    stytch.sso,
    stytchOrg,
  ])

  useEffect(() => {
    authenticate()
  }, [authenticate])

  return (
    <Stack spacing={4} alignItems="center">
      {discoveredOrgs.length ? (
        <Card elevation={2} sx={{ p: 6, width: 450 }}>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            Select an Account
          </Typography>
          <Stack spacing={2}>
            {discoveredOrgs.map((org) => {
              return (
                <Button
                  size="medium"
                  key={org.organization.organization_id}
                  onClick={() => {
                    logInToOrg(org.organization)
                  }}
                >
                  {org.organization.organization_name}
                </Button>
              )
            })}
          </Stack>
        </Card>
      ) : (
        <>
          <CircularProgress size={80} />
          <Typography>Logging in...</Typography>
        </>
      )}
    </Stack>
  )
}
